/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import {
  Form, Button, Checkbox,
} from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import bizGlobal from 'src/redux/action/bizGlobal';
import useMessage from 'src/hooks/useMessage';
import useCountdown from 'src/hooks/useCountdown';
import { useGet, usePost } from 'src/hooks/useFetch';
import { setToken } from 'src/common/login';
import InputBtn from 'src/components/InputBtn';
import { MessageOpt } from 'src/eventEmits/message';
import eventEmitter from 'src/eventEmits';
import logo from 'src/images/logo.png';
import AgreementModal from './AgreementModal';
import lessStyle from './index.module.less';

const { GET_ACCOUNT_TYPE, GET_MENUS_AUTH, GET_AUTH_INFO } = bizGlobal;

const SCENE = 'LOGIN';
// 协议类型
enum AgreementType {
  SERVICE = 'service',
  PRIVACY = 'privacy',
}

const AUTH_URL = '/admin/account-auth';
const AUTH_INTEGRATION_URL = '/admin/account-auth-integration';

const Login: React.FC = function () {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const systemType = searchParams.get('systemType');
  const isShowIntegration = systemType === 'integration';

  const bizGlobalReducers = useSelector((state: any) => state.bizGlobalReducers || {});
  const { authInfo = {}, menusAuth = [], accountType, rechargeVisible } = bizGlobalReducers;
  const messageApi = useMessage();
  const form = Form.useForm()[0];

  const { countdown, isCounting, startCountdown } = useCountdown(60);
  const [agreed, setAgreed] = useState(false); // 是否勾选同意协议
  const [showAgreement, setShowAgreement] = useState(false); // 是否显示协议弹窗
  const [agreementType, setAgreementType] = useState<AgreementType>(AgreementType.SERVICE); // 当前显示的协议类型

  const { fetchData: sendCode, loading: sendCodeLoading } = useGet('/oa-real-estate/api/v1/auth/sendCode');
  const { fetchData: login } = usePost('/oa-real-estate/api/v1/auth/login');

  const onFinish = (values: any) => {
    if (!agreed) {
      messageApi.warning('请先阅读并同意服务协议和隐私政策');
      return;
    }

    // 这里可以添加登录逻辑
    login({ phone: values.phone, code: values.verificationCode, smsScene: SCENE }).then((res) => {
      if (res.isSuccess) {
        setToken(res.data.token);
        if (res.data.roleType === 0 && res.data.statusCode === 0 && res.data.officeId === null) {
          dispatch<any>(GET_ACCOUNT_TYPE('person'));
        }
        dispatch<any>(GET_MENUS_AUTH()).then(() => {
          dispatch<any>(GET_AUTH_INFO()).then(() => {
            if (res.data.roleType === 0 || accountType === 'person') {
              // 团队普通用户或个人用户
              if ([0].includes(res.data.statusCode)) {
                // 未实名认证时，进入引导页
                navigate(isShowIntegration ? AUTH_INTEGRATION_URL : AUTH_URL);
              } else if ([5].includes(res.data.statusCode)) {
                // 5 已律师认证
                if (!res.data.officeId) {
                  // 没绑定律所，之前一定是走的个人流程
                  dispatch<any>(GET_ACCOUNT_TYPE('person'));
                  navigate('/admin/search');
                } else {
                  if (localStorage.getItem('guide_done')) {
                    // 已经浏览过引导页
                    navigate('/admin/search');
                  } else {
                    // 没浏览过的话就打开一次
                    localStorage.setItem('guide_done', '1');
                    navigate(isShowIntegration ? AUTH_INTEGRATION_URL : AUTH_URL);
                  }
                }
              } else {
                // 1 只完成实名认证
                // 2、3、4 是律师认证的中间状态
                if (!res.data?.officeId) {
                  if (localStorage.getItem('processType') === 'office') {
                    dispatch<any>(GET_ACCOUNT_TYPE('office'));
                    navigate('/admin/authenticationOffice?todo=createOffice');
                  } else {
                    // 没绑定律所，之前一定是走的个人流程
                    dispatch<any>(GET_ACCOUNT_TYPE('person'));
                    navigate('/admin/authenticationPerson');
                  }
                } else {
                  navigate('/admin/authentication');
                }
              }
            } else if ([1, 2].includes(res.data.roleType)) {
              // 律所管理员，去用户管理
              navigate('/admin/useManagement');
            } else {
              // 还可能是 null，还没有分配用户角色或接口bug，跳转到引导页
              navigate(isShowIntegration ? AUTH_INTEGRATION_URL : AUTH_URL);
            }
          });
        });
      }
    });
  };

  const getVerificationCode = () => {
    const phone = form.getFieldValue('phone');
    if (!phone || phone.length !== 11) {
      messageApi.warning('请输入手机号');
      return;
    }
    if (isCounting || sendCodeLoading) {
      return;
    }
    sendCode({ phone, scene: SCENE }).then((res) => {
      if (res.isSuccess) {
        startCountdown();
        messageApi.success('验证码发送成功');
      }
    });
  };

  /**
   * 低版本浏览器兼容处理
   * 处理 antd message 组件
   */
  useEffect(() => {
    localStorage.removeItem('token');
    const messageCallback = (option: MessageOpt) => {
      const { type, options: msgOpt } = option;
      messageApi[type](msgOpt);
    };
    eventEmitter.on('message', messageCallback);
    return () => {
      eventEmitter.off('message', messageCallback);
    };
  }, []);

  useEffect(() => {
    if (systemType) {
      localStorage.setItem('systemType', systemType);
    }
  }, [systemType]);

  // 打开协议弹窗
  const openAgreement = (type: AgreementType) => {
    setAgreementType(type);
    setShowAgreement(true);
  };

  // 关闭协议弹窗
  const closeAgreement = () => {
    setShowAgreement(false);
  };

  // 处理协议勾选
  const handleAgreementCheck = (e: any) => {
    setAgreed(e.target.checked);
  };

  return (
    <div className={lessStyle.loginContainer}>
      <div className={lessStyle.innerContainer}>
        <div className={lessStyle.left}>
          <div className={lessStyle.bgBanner} />
          <div className={lessStyle.info}>
            <div className={lessStyle.logo}>
              <img src={logo} alt="" />
            </div>
            <div className={lessStyle.title}>
              <img
                alt=""
                src={require('src/pages/Home/images/banner-title.png')}
              />
            </div>
            <div className={lessStyle.subTitle}>安全&全面&快速</div>
          </div>
        </div>
        <div className={lessStyle.form}>
          <div className={lessStyle.title}>登录/注册</div>
          <Form
            form={form}
            name="login"
            onFinish={onFinish}
            style={{
              width: '100%',
              maxWidth: 400,
            }}
          >
            <Form.Item
              name="phone"
              rules={[
                { required: true, message: '请输入手机号码' },
                { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号码' },
              ]}
              validateTrigger={['onBlur']}
              style={{ marginBottom: 32 }}
            >
              <InputBtn
                isShowIcon={false}
                style={{ width: '100%', padding: '11px 24px' }}
                inputProps={{
                  placeholder: '请输入手机号码',
                  autoComplete: 'off',
                  variant: 'borderless',
                  style: { width: '100%', padding: 0 },
                  size: 'large',
                  maxLength: 11,
                  onChange: (e: any) => {
                    const phone = e.target.value;
                    form.setFieldsValue({ phone });
                  },
                }}
                isShowSearchButton={false}
              />
            </Form.Item>

            <Form.Item
              name="verificationCode"
              rules={[{ required: true, message: '请输入验证码' }, { len: 6, message: '请输入6位验证码' }]}
            >
              <InputBtn
                isShowIcon={false}
                inputProps={{
                  placeholder: '请输入验证码',
                  autoComplete: 'off',
                  variant: 'borderless',
                  style: { width: '100%', padding: 0 },
                  size: 'large',
                  maxLength: 6,
                  onInput: (e: any) => {
                    e.target.value = e.target.value.replace(/\D/g, '');
                  },
                  onChange: (e: any) => {
                    const verificationCode = e.target.value;
                    if (!/^\d*$/.test(e.target.value)) {
                      return;
                    }
                    form.setFieldsValue({ verificationCode });
                  },
                }}
                style={{ width: '100%', padding: '11px 24px' }}
                searchButton={isCounting ? `${countdown}s` : '获取验证码'}
                onClick={getVerificationCode}
                btnStyle={{
                  cursor: isCounting ? 'not-allowed' : 'pointer', fontSize: 16, width: 80, textAlign: 'center',
                }}
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" size="large" htmlType="submit" style={{ width: '100%', borderRadius: 24, marginTop: 24 }} autoInsertSpace={false}>
                登录
              </Button>
            </Form.Item>

            <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
              <Checkbox checked={agreed} onChange={handleAgreementCheck}>
                我已阅读并同意
                <span
                  style={{ color: '#1677ff', cursor: 'pointer' }}
                  onClick={(e) => { e.preventDefault(); openAgreement(AgreementType.SERVICE); }}
                >
                  《服务协议》
                </span>
                和
                <span
                  style={{ color: '#1677ff', cursor: 'pointer' }}
                  onClick={(e) => { e.preventDefault(); openAgreement(AgreementType.PRIVACY); }}
                >
                  《隐私政策》
                </span>
              </Checkbox>
            </Form.Item>
          </Form>
        </div>
      </div>

      {/* 协议弹窗 */}
      <AgreementModal
        visible={showAgreement}
        onClose={closeAgreement}
        type={agreementType}
        onAgree={() => {
          setAgreed(true);
          closeAgreement();
        }}
      />
    </div>
  );
};

export default Login;
